import { Button, Divider, Form, Input, message, Select, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import './CampaignBrief.css';
import { useEffect, useState } from 'react';
import { fetchRequest } from '../../../utils/Fetcher';

const requiredFields = [
  "vehicle",
  "targetAudience",
  "keySellingPoints",
  "desiredTone",
  "objective",
  "useBrandAsset",
];

const toTextareaValue = (value) => {
  if (Array.isArray(value)) return value.join('\n');
  return value ?? '';
};

const CampaignBrief = ({ onNext, initialData }) => {
    const desc = "Set up campaign brief";

    const [messageApi, contextHolder] = message.useMessage();    
    const [form] = Form.useForm();
    const [canNext, setCanNext] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const checkValid = () => {
        const values = form.getFieldsValue(true);

        const requiredFilled = requiredFields.every((k) => {
            const v = values?.[k];
            if (Array.isArray(v)) return v.length > 0;
            return v !== undefined && v !== null && String(v).trim() !== "";
        });

        const hasErrors = form
            .getFieldsError(requiredFields)
            .some(({ errors }) => errors.length > 0);

        setCanNext(requiredFilled && !hasErrors);
    };

    useEffect(() => {
        if (!initialData) return;

        form.setFieldsValue({
            vehicle: initialData.vehicle ?? "",
            targetAudience: toTextareaValue(initialData.targetAudience),
            keySellingPoints: toTextareaValue(initialData.keySellingPoints),
            desiredTone: toTextareaValue(initialData.desiredTone),
            objective: toTextareaValue(initialData.objective),
            useBrandAsset: initialData.use_brand_asset ?? initialData.useBrandAsset ?? "Yes",
        });

        checkValid();
    }, [initialData, form]);

    const handleFieldsChange = () => checkValid();

    const onFinish = async (values) => {
        try {
            setSubmitting(true);

            const payload = {
            ...values,
            targetAudience: values.targetAudience ?? "",
            keySellingPoints: values.keySellingPoints ?? "",
            desiredTone: values.desiredTone ?? "",
            objective: values.objective ?? "",
            useBrandAsset: values.useBrandAsset,
            };

            const response = await fetchRequest({
            method: "POST",
            path: `sessions/campaign-brief/generate`,
            data: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error("API failed");

            onNext(values);
        } catch (e) {
            messageApi.error(e.message);
        } finally {
            setSubmitting(false);
        }
    };
    
    return (
        <div className="campaign-brief-container">
            {contextHolder}
            <div className="content-title-container">
                <div className="content-title">
                    {desc}
                    <Tooltip placement="bottom" title={process.env.REACT_APP_TOOLTIP_CAMPAIGN_BRIEF}>
                        <QuestionCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#888', cursor: 'pointer' }} />
                    </Tooltip>
                </div>
                <Divider className="content-title-divider"/>
            </div>
            <Form
                form={form} 
                onFinish={onFinish}
                onFieldsChange={handleFieldsChange}
            >
                <div className="campaign-brief-content-container">
                    <Form.Item
                        label="Vehicle"
                        name="vehicle"
                        className="brief-campaign-input"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Target Audience"
                        name="targetAudience"
                        className="brief-campaign-textarea"
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea autoSize={{ minRows: 2, maxRows: 3 }} />
                    </Form.Item>

                    <Form.Item
                        label="Key Selling Point"
                        name="keySellingPoints"
                        className="brief-campaign-textarea"
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea autoSize={{ minRows: 2, maxRows: 3 }} />
                    </Form.Item>

                    <Form.Item
                        label="Desired Tone & Mood"
                        name="desiredTone"
                        className="brief-campaign-input"
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
                    </Form.Item>

                    <Form.Item
                        label="Objective"
                        name="objective"
                        className="brief-campaign-textarea"
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea autoSize={{ minRows: 2, maxRows: 3 }} />
                    </Form.Item>

                    <Form.Item
                        label="Use Brand Asset"
                        name="useBrandAsset"
                        initialValue={"Yes"}
                        className="brief-campaign-textarea"
                        rules={[{ required: true }]}
                    >
                        <Select options={[
                            { value: "Yes", label: "Yes" },
                            { value: "No", label: "No" },
                        ]} />
                    </Form.Item>

                    {canNext && (
                        <div className="back-next-button">
                            <Button htmlType="submit" loading={submitting}>
                                Next
                            </Button>
                        </div>
                    )}
                </div>
            </Form>
        </div>
    );
};

export default CampaignBrief;